import React, { Fragment, useEffect, useState } from 'react';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import {
    FeatureHero,
    FeatureHeroCta,
    FeatureSection,
    FeatureDownloadCta
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './ClientsMain.css';

/* ---------- Inline SVG icons (no FontAwesome dependency) ---------- */

const STAR_ICON = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2l2.9 6.26L21.5 9.3l-4.75 4.63L17.9 20.5 12 17.27 6.1 20.5l1.15-6.57L2.5 9.3l6.6-1.04L12 2z"/>
    </svg>
);

const CHECK_ICON = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const SIGNAL_ICON = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <rect x="2" y="14" width="4" height="7" rx="1"/>
        <rect x="9" y="9" width="4" height="12" rx="1"/>
        <rect x="16" y="4" width="4" height="17" rx="1"/>
    </svg>
);

const WIFI_ICON = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
        <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <line x1="12" y1="20" x2="12.01" y2="20"/>
    </svg>
);

const BATTERY_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="16" height="10" rx="2"/>
        <line x1="22" y1="11" x2="22" y2="13"/>
        <rect x="4" y="9" width="12" height="6" rx="1" fill="currentColor" stroke="none"/>
    </svg>
);

const USER_CIRCLE_ICON = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="12" cy="10" r="3"/>
        <path d="M6.5 19a6 6 0 0 1 11 0"/>
    </svg>
);

const PLUS_ICON = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/>
        <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);

const HOME_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 10.5 12 3l9 7.5"/>
        <path d="M5 9.5V21h14V9.5"/>
    </svg>
);

const USERS_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
);

const WAND_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 4V2"/>
        <path d="M15 16v-2"/>
        <path d="M8 9h2"/>
        <path d="M20 9h2"/>
        <path d="M17.8 11.8 19 13"/>
        <path d="M15 9h0"/>
        <path d="M17.8 6.2 19 5"/>
        <path d="m3 21 9-9"/>
        <path d="M12.2 6.2 11 5"/>
    </svg>
);

const ARCHIVE_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="5" rx="1"/>
        <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/>
        <line x1="10" y1="12" x2="14" y2="12"/>
    </svg>
);

const ELLIPSIS_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <circle cx="5" cy="12" r="2"/>
        <circle cx="12" cy="12" r="2"/>
        <circle cx="19" cy="12" r="2"/>
    </svg>
);

const ARROW_LEFT_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12"/>
        <polyline points="12 19 5 12 12 5"/>
    </svg>
);

const CHEVRON_RIGHT_ICON = (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);

const MESSAGE_ICON = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
);

const PHONE_ICON = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
);

const ENVELOPE_ICON = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2"/>
        <polyline points="22 6 12 13 2 6"/>
    </svg>
);

const EXTERNAL_ICON = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
);

const STATEMENT_ICON = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="8" y1="13" x2="16" y2="13"/>
        <line x1="8" y1="17" x2="13" y2="17"/>
    </svg>
);

const ADDRESS_BOOK_ICON = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        <circle cx="12" cy="9" r="2.5"/>
        <path d="M8.5 15a3.5 3.5 0 0 1 7 0"/>
    </svg>
);

const NOTE_ICON = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9.5L21 14.5V5a2 2 0 0 0-2-2z"/>
        <path d="M14.5 21v-6.5H21"/>
        <line x1="7" y1="8" x2="13" y2="8"/>
        <line x1="7" y1="12" x2="11" y2="12"/>
    </svg>
);

const CLIENT_NAMES = [
    'Isabella Martin',
    'Julian Styles',
    'Emily Wilson',
    'Alexander Kim',
    'Lucas Brooks',
    'Sophia Patel',
    'Caleb Jenkins'
];

const DUE_INVOICES = [
    { id: 'ES0028', date: '01 Sep', statusKey: 'dueIn5', amount: 2149.88, tone: 'green' },
    { id: 'ES0014', date: '16 Jun', statusKey: 'dueIn2', amount: 392, tone: 'orange' },
    { id: 'ES0009', date: '01 Apr', statusKey: 'overdue2', amount: 134.2, tone: 'red' },
    { id: 'ES0005', date: '30 Mar', statusKey: 'overdue170', amount: 100, tone: 'red' }
];

const TOOL_ICONS = [STATEMENT_ICON, ADDRESS_BOOK_ICON, NOTE_ICON];
const TOOL_TONES = ['emerald', 'blue', 'amber'];

function ClientsMain(){
    const { t, locale } = useLocale();
    const { formatCurrency } = useCurrency();
    const p = (key) => t(`featurePages.clients.${key}`);

    const[loader, SetLoader] = useState(false)

    useEffect(() => {
        const timer = setTimeout(() => {
            SetLoader(true)
        }, 1000)

        return () => clearTimeout(timer)
    }, [])

    if (!loader) {
        return <Loader/>;
    }

    const rosterBullets = [p('rosterBullet1'), p('rosterBullet2'), p('rosterBullet3')];
    const hubBullets = [p('hubBullet1'), p('hubBullet2'), p('hubBullet3')];
    const tools = [1, 2, 3].map((n, i) => ({
        icon: TOOL_ICONS[i],
        tone: TOOL_TONES[i],
        title: p(`tool${n}Title`),
        description: p(`tool${n}Description`)
    }));

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.clients} locale={locale} />

            <FeatureHero
                className="cl-hero"
                eyebrow={<><span className="fc-hero-eyebrow-icon">{STAR_ICON}</span>{p('eyebrow')}</>}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                cta={
                    <FeatureHeroCta
                        primaryLabel={t('shared.featureStartFree')}
                        primaryTarget="feature-download"
                        secondaryLabel={t('shared.featureSeeHowItWorks')}
                        secondaryTarget="feature-content"
                    />
                }
            />

            {/* The Client Roster */}
            <FeatureSection id="feature-content" className="cl-roster-section">
                <div className="cl-split">
                    <div className="cl-split-text">
                        <h2 className="cl-split-title">{p('rosterTitle')}</h2>
                        <p className="cl-split-desc">{p('rosterDescription')}</p>
                        <ul className="cl-bullets">
                            {rosterBullets.map((bullet, idx) => (
                                <li className="cl-bullet" key={idx}>
                                    <span className="cl-bullet-icon">{CHECK_ICON}</span>
                                    <span className="cl-bullet-text">{bullet}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="cl-split-visual">
                        <div className="cl-phone cl-float">
                            <div className="cl-status">
                                <span>22:47</span>
                                <div className="cl-status-icons">
                                    {SIGNAL_ICON}
                                    {WIFI_ICON}
                                    {BATTERY_ICON}
                                </div>
                            </div>

                            <div className="cl-list-header">
                                <h2 className="cl-list-title">{p('rosterPhoneTitle')}</h2>
                                <button type="button" className="cl-pill-btn" tabIndex={-1}>{p('rosterPhoneAdd')}</button>
                            </div>

                            <div className="cl-list cl-scroll">
                                {CLIENT_NAMES.map((name) => (
                                    <div className="cl-list-item" key={name}>
                                        <span className="cl-list-item-icon">{USER_CIRCLE_ICON}</span>
                                        <span className="cl-list-item-name">{name}</span>
                                    </div>
                                ))}
                            </div>

                            <button type="button" className="cl-fab" tabIndex={-1} aria-hidden="true">{PLUS_ICON}</button>

                            <div className="cl-bottom-nav">
                                <div className="cl-nav-item">{HOME_ICON}<span>{p('navHome')}</span></div>
                                <div className="cl-nav-item cl-nav-item-active">{USERS_ICON}<span>{p('navClients')}</span></div>
                                <div className="cl-nav-item">{WAND_ICON}<span>{p('navAi')}</span></div>
                                <div className="cl-nav-item">{ARCHIVE_ICON}<span>{p('navInvoices')}</span></div>
                                <div className="cl-nav-item">{ELLIPSIS_ICON}<span>{p('navMore')}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </FeatureSection>

            {/* 360° Client Hub */}
            <FeatureSection className="cl-hub-section">
                <div className="cl-split cl-split-reverse">
                    <div className="cl-split-text">
                        <h2 className="cl-split-title">{p('hubTitle')}</h2>
                        <p className="cl-split-desc">{p('hubDescription')}</p>
                        <ul className="cl-bullets">
                            {hubBullets.map((bullet, idx) => (
                                <li className="cl-bullet" key={idx}>
                                    <span className="cl-bullet-icon">{CHECK_ICON}</span>
                                    <span className="cl-bullet-text">{bullet}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="cl-split-visual">
                        <div className="cl-phone cl-float-delayed">
                            <div className="cl-status">
                                <span>22:48</span>
                                <div className="cl-status-icons">
                                    {SIGNAL_ICON}
                                    {WIFI_ICON}
                                    {BATTERY_ICON}
                                </div>
                            </div>

                            <div className="cl-hub-header">
                                <button type="button" className="cl-back-btn" tabIndex={-1} aria-hidden="true">{ARROW_LEFT_ICON}</button>
                                <button type="button" className="cl-pill-btn" tabIndex={-1}>{p('hubPhoneEdit')}</button>
                            </div>

                            <div className="cl-hub-title">
                                <h2>{p('hubPhoneName')}</h2>
                            </div>

                            <div className="cl-tabs">
                                <div className="cl-tab cl-tab-active">{p('hubTabActivity')}</div>
                                <div className="cl-tab">{p('hubTabInfo')}</div>
                            </div>

                            <div className="cl-hub-content cl-scroll">
                                <p className="cl-section-label">{p('hubPaymentStatusLabel')}</p>
                                <div className="cl-card">
                                    <div className="cl-payment-row">
                                        <span>{p('hubPaidValue')}</span>
                                        <span>{formatCurrency(p('hubTotalValue'))}</span>
                                    </div>
                                    <div className="cl-progress">
                                        <div className="cl-progress-fill"></div>
                                    </div>
                                </div>

                                <p className="cl-section-label">{p('hubDueInvoicesLabel')}</p>
                                <div className="cl-card">
                                    <div className="cl-invoice-list">
                                        {DUE_INVOICES.map((invoice) => (
                                            <div className="cl-invoice-row" key={invoice.id}>
                                                <div>
                                                    <div className="cl-invoice-id">{invoice.id}</div>
                                                    <div className="cl-invoice-meta">
                                                        <span>{invoice.date} - </span>
                                                        <span className={`cl-invoice-status-${invoice.tone}`}>{p(invoice.statusKey)}</span>
                                                    </div>
                                                </div>
                                                <div className="cl-invoice-right">
                                                    <span className="cl-invoice-amount">{formatCurrency(invoice.amount)}</span>
                                                    <span className="cl-invoice-chevron">{CHEVRON_RIGHT_ICON}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="cl-action-bar">
                                <div className="cl-action">{MESSAGE_ICON}<span>{p('hubActionMessage')}</span></div>
                                <div className="cl-action">{PHONE_ICON}<span>{p('hubActionCall')}</span></div>
                                <div className="cl-action">{ENVELOPE_ICON}<span>{p('hubActionEmail')}</span></div>
                                <div className="cl-action">{EXTERNAL_ICON}<span>{p('hubActionCreate')}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </FeatureSection>

            {/* Tools grid */}
            <FeatureSection
                className="cl-tools-section"
                title={p('toolsTitle')}
                description={p('toolsDescription')}
            >
                <div className="cl-tools-grid">
                    {tools.map((tool) => (
                        <div className="cl-tool-card" key={tool.title}>
                            <div className={`cl-tool-icon cl-tool-icon-${tool.tone}`}>{tool.icon}</div>
                            <h3 className="cl-tool-title">{tool.title}</h3>
                            <p className="cl-tool-desc">{tool.description}</p>
                        </div>
                    ))}
                </div>
            </FeatureSection>

            <FeatureDownloadCta
                className="cl-download-cta"
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaLabel={p('ctaButton')}
                note={p('ctaNote')}
                storesLabel={t('shared.featureDownloadApp')}
            />

            <Footer />
        </Fragment>
    )
}

export default ClientsMain;